import React, { useRef, useState, useEffect } from 'react';

import { cn } from '@easydatasolution/ui-react/lib/utils';
import { FaChevronUp } from '@react-icons/all-files/fa/FaChevronUp';

import { DiamondNumber } from './DiamondNumber';
import { MarkdownRaw } from './MarkdownRaw';

interface StepsProps {
	items: {
		title: string;
		text: string;
	}[];
	className?: string;
}

export default function Steps({ items, className }: StepsProps) {
	return (
		<div className={cn(`relative my-6`, className)}>
			{/* line above */}
			<div className="before:absolute before:left-[30px] before:z-10 before:-mt-12 before:h-[4.5rem] before:-translate-x-1/2 before:translate-y-3 before:self-start before:bg-gradient-to-b before:from-transparent before:to-slate-200 before:px-0.5 md:before:left-1/2"></div>

			{items.map((item, index) => (
				<div key={index} className="group relative py-6 pl-[4.5rem] md:pl-0">
					{/* dot (after) + line (before) after it */}
					<div className="before:absolute before:left-[30px] before:z-10 before:h-full before:-translate-x-1/2 before:translate-y-3 before:self-start before:bg-slate-200 before:px-0.5 after:absolute after:left-[30px] after:z-10 after:box-content after:h-4 after:w-4 after:-translate-x-1/2 after:translate-y-1.5 after:rounded-full after:border-4 after:border-white after:bg-primary-500 md:before:left-1/2 md:after:left-1/2">
						{/* content left from line */}
						<div className="absolute left-2 flex flex-row items-center space-x-5 text-secondary-700 md:left-8">
							<DiamondNumber number={index + 1} />
							<div className="hidden text-xl font-bold sm:max-w-[16rem] md:block xl:max-w-[24rem]">
								{item.title}
							</div>
						</div>
					</div>

					{/* content right from line*/}
					<div className="flex flex-col space-y-3 md:ml-[50%] md:space-y-0 md:pl-8">
						<div className="text-xl font-bold sm:max-w-[16rem] md:hidden xl:max-w-[24rem]">
							{item.title}
						</div>
						<StepText>{item.text}</StepText>
					</div>
				</div>
			))}
		</div>
	);
}

type StepTextProps = {
	children: React.ReactNode;
};

function StepText({ children }: StepTextProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isTruncated, setIsTruncated] = useState(false);
	const textRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const checkTruncation = () => {
			if (textRef.current) {
				const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight);
				const maxHeight = lineHeight * 4; // match line-clamp-4
				setIsTruncated(textRef.current.scrollHeight > maxHeight);
			}
		};

		checkTruncation();
		window.addEventListener('resize', checkTruncation);
		return () => window.removeEventListener('resize', checkTruncation);
	}, [children]);

	return (
		<div>
			<div
				ref={textRef}
				className={`overflow-hidden text-lg text-secondary-700 transition-all duration-300 ${
					isExpanded ? '' : 'line-clamp-4'
				}`}
			>
				<MarkdownRaw>{children as string}</MarkdownRaw>
			</div>
			{isTruncated && (
				<button
					onClick={() => setIsExpanded(!isExpanded)}
					className="mt-4 block text-sm font-semibold text-slate-400 hover:text-slate-500"
				>
					{isExpanded ? (
						<span className="inline-flex items-center gap-1">
							weniger anzeigen <FaChevronUp />
						</span>
					) : (
						<span className="inline-flex items-center gap-1">
							mehr anzeigen <FaChevronUp className="rotate-180" />
						</span>
					)}
				</button>
			)}
		</div>
	);
}
